import { type Location, routes } from "@src/appV2/redesign/utils/routes";

export type SessionRole = "admin" | "worker" | "workplace";

/**
 * Discriminated by `role`: admin and worker have static homes and ignore
 * workplace flags entirely; workplace requires the four flags that govern
 * its landing page (Home Health → Schedule → Facility dashboard fallback).
 *
 * Home Health workplaces land on Cases on desktop and on the mobile
 * unsupported page on mobile (native Capacitor OR narrow viewport). HH
 * workplace surfaces aren't designed for mobile yet, so the unsupported
 * page is a dead end with only a logout action — see
 * `HomeHealthMobileUnsupportedPage` and the matching gate in
 * `NavigationMenuContainer`.
 *
 * Historically the HH branch was gated by `!isMobileViewport`, which fell
 * HH-on-mobile through to `/schedule` and produced an infinite redirect
 * loop via `PrivateRoute` → `/enter` → `renderStartPage` (TECH-1370).
 * Routing HH-on-mobile to a dedicated dead-end page is the long-term fix.
 *
 * `isMobile` here is the OR of `useIsMobile()` (viewport breakpoint) and
 * `Platform.isMobile` (Capacitor native check). The caller is responsible
 * for combining the two.
 */
export type GetHomeRouteForSessionParams =
  | { role: "admin" }
  | { role: "worker" }
  | {
      role: "workplace";
      isMobile: boolean;
      isHomeHealthEnabled: boolean;
      isScheduleTabEnabled: boolean;
      isMonthlyViewEnabled: boolean;
    };

/**
 * Resolves the landing route for a given session shape. Single source of
 * truth for "where does this user type belong when no other URL is in
 * play" — used by `renderStartPage` and by `useSwitchUser` so that
 * context switches navigate directly to the new role's home instead of
 * bouncing through `/enter` against stale Redux state (ORB-1847).
 */
export function getHomeRouteForSession(params: GetHomeRouteForSessionParams): Location {
  if (params.role === "admin") {
    return { pathname: routes.adminAccountsAgents };
  }

  if (params.role === "worker") {
    return { pathname: routes.agentDashboard };
  }

  const { isHomeHealthEnabled, isScheduleTabEnabled, isMobile } = params;

  if (isHomeHealthEnabled) {
    return { pathname: isMobile ? routes.homeHealthMobileUnsupported : routes.homeHealthCases };
  }

  if (isScheduleTabEnabled) {
    return { pathname: routes.schedule };
  }

  return { pathname: routes.facilityDashboard };
}

export interface SessionRoleFlags {
  isAdmin: boolean;
  isWorker: boolean;
  isWorkplace: boolean;
}

/**
 * Resolves a `SessionRole` from the boolean flags computed in
 * `mapStateToProps` (admin, worker, workplace). Used by `renderStartPage`
 * — for post-`pseudo()` derivation from a live Redux session, see
 * `useSwitchUser` instead.
 */
export function getSessionRoleFromFlags(flags: SessionRoleFlags): SessionRole | undefined {
  const { isAdmin, isWorker, isWorkplace } = flags;

  if (isAdmin) {
    return "admin";
  }
  if (isWorker) {
    return "worker";
  }
  if (isWorkplace) {
    return "workplace";
  }
  return undefined;
}
